if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      quantityInputSticky = undefined;
      quantityFormSticky = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      sectionCache = new Map();
      latestRequestId = 0;
      pendingRequestUrl = null;
      optimisticSnapshot = null;
      abortController = undefined;
      cacheCartUpdateUnsubscriber = undefined;
      prefetchTimer = null;
      prefetchInFlight = 0;
      prefetchDelay = 120;
      cacheTtl = 30000;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input-primary');
        this.quantityInputSticky = this.querySelector('.quantity__input-sticky');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        // pointerover/pointerout and focusin/focusout are the bubbling counterparts of
        // pointerenter/leave and focus/blur, which allows delegation from this element.
        this.abortController = new AbortController();
        const listenerOptions = { passive: true, signal: this.abortController.signal };
        ['pointerover', 'pointerout', 'pointerdown', 'focusin', 'focusout'].forEach((type) =>
          this.addEventListener(type, this.handlePrefetchEvent.bind(this), listenerOptions)
        );

        if (typeof PUB_SUB_EVENTS.cartUpdate !== 'undefined') {
          this.cacheCartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.sectionCache.clear());
        }

        this.initQuantityHandlers();
        this.confirmedSelection = this.currentSelection();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      // Ids of the checked option inputs; the last server-confirmed set is what a failed request
      // goes back to, so the visible selection always matches the variant id in the form.
      currentSelection() {
        return Array.from(this.querySelectorAll('variant-selects input[type="radio"]:checked'))
          .map((input) => input.id)
          .filter(Boolean);
      }

      restoreSelection(ids) {
        if (!ids) return;
        ids.forEach((id) => {
          const input = document.getElementById(id);
          if (input) input.checked = true;
        });
        this.querySelectorAll('variant-selects fieldset').forEach((fieldset) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          const node = fieldset.querySelector('legend [data-selected-value]');
          if (!checked || !node) return;
          node.textContent = checked.value;
        });
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput && !this.quantityInputSticky) return;

        this.quantityForm = this.querySelector('.product-form__quantity-primary');
        this.quantityFormSticky = this.querySelector('.product-form__quantity-sticky');
        if (!this.quantityForm && !this.quantityFormSticky) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
        this.cacheCartUpdateUnsubscriber?.();
        this.abortController?.abort();
        this.cancelPrefetch();
        this.sectionCache.clear();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.cancelPrefetch();

        // Optimism only when nothing else is in flight: a second fast click must not show a
        // combination built on top of a selection that has not been resolved yet. It runs before
        // resetProductFormState so its snapshot holds the buttons' pre-click state.
        if (this.pendingRequestUrl === null) {
          this.optimisticSnapshot = this.applyOptimisticAvailability(target);
        }

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = (this.dataset.url !== productUrl) || this.querySelector('li[data-gang-option]') !== null;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        // Responses may be shared with a prefetch, so they are never aborted; a newer request
        // simply makes older ones stale and their result is dropped.
        const requestId = (this.latestRequestId = (this.latestRequestId || 0) + 1);

        this.fetchSectionText(requestUrl)
          .then((responseText) => {
            if (requestId !== this.latestRequestId) return;
            this.pendingRequestUrl = null;
            const snapshot = this.optimisticSnapshot;
            this.optimisticSnapshot = null;
            this.sectionCache.clear();
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            this.releasePendingButtons(snapshot);
            this.confirmedSelection = this.currentSelection();
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            console.error(error);
            if (requestId !== this.latestRequestId) return;
            this.pendingRequestUrl = null;
            this.restoreSnapshot(this.optimisticSnapshot);
            this.optimisticSnapshot = null;
            this.restoreSelection(this.confirmedSelection);
            // The form still holds the confirmed variant: buttons follow its server-rendered state.
            [
              this.querySelector(`#ProductSubmitButton-${this.sectionId}`),
              this.querySelector(`#StickyProductSubmitButton-${this.sectionId}`),
            ]
              .filter(Boolean)
              .forEach((button) => {
                button.removeAttribute('data-opt-pending');
                button.removeAttribute('aria-busy');
                const soldOut = button.getAttribute('data-availability-state') === 'sold_out';
                button.toggleAttribute('disabled', soldOut);
                if (soldOut) {
                  button.setAttribute('aria-disabled', 'true');
                } else {
                  button.removeAttribute('aria-disabled');
                }
              });
          });
      }

      // The server did not refresh a button the optimistic pass left disabled: put it back.
      releasePendingButtons(snapshot) {
        const pending = Array.from(this.querySelectorAll('[data-opt-pending]'));
        if (!pending.length) return;
        this.restoreSnapshot((snapshot || []).filter(({ node }) => pending.includes(node)));
        pending.forEach((button) => {
          button.removeAttribute('data-opt-pending');
          button.removeAttribute('aria-busy');
          button.removeAttribute('aria-disabled');
        });
      }

      // Small in-memory cache (by URL) shared by prefetch and the real request; entries expire.
      fetchSectionText(requestUrl) {
        const cached = this.sectionCache.get(requestUrl);
        if (cached && cached.expires > Date.now()) return cached.promise;
        this.sectionCache.delete(requestUrl);

        const promise = fetch(requestUrl).then((response) => {
          if (!response.ok) throw new Error(`Section request failed with status ${response.status}`);
          return response.text();
        });
        const entry = { promise, expires: Date.now() + this.cacheTtl };
        promise.catch(() => {
          if (this.sectionCache.get(requestUrl) === entry) this.sectionCache.delete(requestUrl);
        });
        this.sectionCache.set(requestUrl, entry);
        if (this.sectionCache.size > 10) {
          this.sectionCache.delete(this.sectionCache.keys().next().value);
        }
        return promise;
      }

      cancelPrefetch() {
        clearTimeout(this.prefetchTimer);
        this.prefetchTimer = null;
      }

      handlePrefetchEvent(event) {
        if (event.type === 'pointerout' || event.type === 'focusout') {
          this.cancelPrefetch();
          return;
        }
        // Touch has no hover: start immediately on pointerdown. Mouse pointerdown is just the click.
        if (event.type === 'pointerdown' && event.pointerType !== 'touch') return;

        const immediate = event.type === 'pointerdown';
        this.cancelPrefetch();
        const request = this.getPrefetchUrl(event.target);
        if (!request) return;

        if (immediate) {
          this.startPrefetch(request);
        } else {
          this.prefetchTimer = setTimeout(() => this.startPrefetch(request), this.prefetchDelay);
        }
      }

      startPrefetch(requestUrl) {
        this.prefetchTimer = null;
        const cached = this.sectionCache.get(requestUrl);
        if (cached && cached.expires > Date.now()) return;
        if (this.prefetchInFlight >= 2) return;

        this.prefetchInFlight++;
        this.fetchSectionText(requestUrl)
          .catch(() => {})
          .finally(() => {
            this.prefetchInFlight = Math.max(0, this.prefetchInFlight - 1);
          });
      }

      getPrefetchUrl(node) {
        if (navigator.connection?.saveData) return null;
        if (!(node instanceof Element)) return null;
        const input = node.matches('input[type="radio"]') ? node : node.closest('label')?.control;
        if (!input || !input.matches('variant-selects[data-primary] fieldset input[type="radio"]')) return null;
        if (input.checked || input.disabled || !this.contains(input)) return null;

        const productUrl = input.dataset.productUrl || this.dataset.url;
        // Only the in-place section update is prefetched; product swaps fetch a full page.
        const shouldSwapProduct = this.dataset.url !== productUrl || this.querySelector('li[data-gang-option]') !== null;
        if (shouldSwapProduct) return null;

        const fieldset = input.closest('fieldset');
        const selectedOptionValues = Array.from(
          input.closest('variant-selects').querySelectorAll('select option[selected], fieldset input:checked')
        ).map((element) => (element.closest('fieldset') === fieldset ? input : element).dataset.optionValueId);
        if (!selectedOptionValues.includes(input.dataset.optionValueId)) return null;

        return this.buildRequestUrlWithParams(productUrl, selectedOptionValues, false);
      }

      // Optimistic UI: values are rendered by Liquid on the card (availability-state snippet), so
      // no rule is duplicated here. The server response still overwrites everything afterwards.
      // Never changes the variant id: both buy buttons stay disabled (and busy) until the server
      // response runs updateVariantInputs + updateProductSubmitButtons. Returns a snapshot of
      // everything it touches so a failed request can put the pre-click state back.
      applyOptimisticAvailability(target) {
        const data = target?.dataset;
        if (!data || !data.optState) return null;

        const buttons = [
          this.querySelector(`#ProductSubmitButton-${this.sectionId}`),
          this.querySelector(`#StickyProductSubmitButton-${this.sectionId}`),
        ].filter(Boolean);
        const boxes = Array.from(this.querySelectorAll('.product-variant-availability'));
        const factValues = Array.from(
          document.querySelectorAll(`#ProductFacts-${this.sectionId} .pdp-facts__value[data-availability-state]`)
        );
        const fieldset = target.closest('fieldset');
        const legendNodes = fieldset?.classList.contains('product-form__input--finish-cards')
          ? Array.from(fieldset.querySelectorAll('legend [data-selected-value]'))
          : [];

        const nodes = [
          ...boxes,
          ...buttons,
          ...factValues,
          ...factValues.map((value) => value.closest('.pdp-facts__item')).filter(Boolean),
          ...legendNodes,
        ];
        const snapshot = nodes.map((node) => ({
          node,
          html: node.innerHTML,
          attrs: Array.from(node.attributes).map((attr) => [attr.name, attr.value]),
        }));

        const state = data.optState;
        const message = (data.optMessage || '').trim();
        const label = (data.optLabel || '').trim();
        const buttonText = (data.optButton || '').trim();

        this.querySelectorAll('.product-variant-availability').forEach((box) => {
          Array.from(box.classList)
            .filter((name) => name.startsWith('product-variant-availability--'))
            .forEach((name) => box.classList.remove(name));
          box.classList.add(`product-variant-availability--${state.replace(/_/g, '-')}`);
          box.setAttribute('data-availability-state', state);

          const status = box.querySelector('.product-variant-availability__status');
          if (status) status.textContent = label;

          let messageNode = box.querySelector('.product-variant-availability__message');
          if (message && !messageNode) {
            messageNode = document.createElement('span');
            messageNode.className = 'product-variant-availability__message';
            box.appendChild(messageNode);
          }
          if (messageNode) {
            if (message) {
              messageNode.textContent = message;
            } else {
              messageNode.remove();
            }
          }
        });

        buttons.forEach((button) => {
            button.setAttribute('disabled', '');
            button.setAttribute('aria-disabled', 'true');
            button.setAttribute('aria-busy', 'true');
            button.setAttribute('data-opt-pending', '');
            button.toggleAttribute('data-pre-order', state === 'pre_order');
            button.setAttribute('data-availability-state', state);
            if (!buttonText) return;
            const buttonLabel = button.querySelector('span');
            if (buttonLabel) {
              buttonLabel.textContent = buttonText;
            } else {
              button.textContent = buttonText;
            }
          });

        factValues.forEach((value) => {
          value.setAttribute('data-availability-state', state);
          value.textContent = message;
          value.closest('.pdp-facts__item')?.classList.toggle('hidden', state === 'sold_out' || !message);
        });

        // The dash before the finish name (non-reference layouts) comes from CSS, so the text is the bare value.
        legendNodes.forEach((node) => {
          node.textContent = target.value;
        });

        return snapshot;
      }

      restoreSnapshot(snapshot) {
        snapshot?.forEach(({ node, html, attrs }) => {
          Array.from(node.attributes).forEach((attr) => node.removeAttribute(attr.name));
          attrs.forEach(([name, value]) => node.setAttribute(name, value));
          node.innerHTML = html;
        });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects[data-primary]');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
        const variantSelectsAlt = html.querySelector('variant-selects[data-sticky]');
        if (variantSelectsAlt) {
          HTMLUpdateUtility.viewTransition(this.variantSelectorsAlt, variantSelectsAlt, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Price-Alt');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateCustomLiquid(html);
          
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.updateProductSubmitButtons(html);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          document.dispatchEvent(new CustomEvent('variant:change', {
            detail: {
              sectionId: this.sectionId,
              variant,
              html,
            },
          }));
        };
      }

      updateCustomLiquid(html) {
        this.querySelectorAll('.custom__liquid').forEach(block => {
          if (block.id) {
            const liquid = html.querySelector(`#${block.id}`);
            if (liquid) block.innerHTML = liquid.innerHTML;
          }
        });
      }

      updateProductSubmitButtons(html) {
        const sourceButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        if (!sourceButton) return;

        const sourceLabel = sourceButton.querySelector('span')?.textContent?.trim() || sourceButton.textContent.trim();
        const isDisabled = sourceButton.hasAttribute('disabled');
        const isPreOrder = sourceButton.hasAttribute('data-pre-order');
        const availabilityState = sourceButton.getAttribute('data-availability-state');

        [
          this.querySelector(`#ProductSubmitButton-${this.sectionId}`),
          this.querySelector(`#StickyProductSubmitButton-${this.sectionId}`),
        ]
          .filter(Boolean)
          .forEach((button) => {
            button.removeAttribute('data-opt-pending');
            button.removeAttribute('aria-busy');
            button.removeAttribute('aria-disabled');
            button.toggleAttribute('disabled', isDisabled);
            button.toggleAttribute('data-pre-order', isPreOrder);
            if (availabilityState) {
              button.setAttribute('data-availability-state', availabilityState);
            } else {
              button.removeAttribute('data-availability-state');
            }

            const label = button.querySelector('span');
            if (label) {
              label.textContent = sourceLabel;
            } else {
              button.textContent = sourceLabel;
            }
          });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}, #product-form-${this.dataset.section}--alt`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, theme.variantStrings.unavailable);

        // A missing combination is not purchasable anywhere: keep the sticky button in step
        // with the main one and drop the stale availability state from both.
        [
          this.querySelector(`#ProductSubmitButton-${this.sectionId}`),
          this.querySelector(`#StickyProductSubmitButton-${this.sectionId}`),
        ]
          .filter(Boolean)
          .forEach((button) => {
            button.setAttribute('disabled', '');
            button.removeAttribute('data-pre-order');
            button.setAttribute('data-availability-state', 'sold_out');
            const label = button.querySelector('span');
            if (label) {
              label.textContent = theme.variantStrings.unavailable;
            } else {
              button.textContent = theme.variantStrings.unavailable;
            }
          });

        // The delivery time belongs to a variant that is no longer selected.
        this.querySelectorAll('.pdp-facts__value[data-availability-state]').forEach((value) => {
          value.closest('.pdp-facts__item')?.classList.add('hidden');
        });

        const selectors = ['price', 'Price-Alt', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul') || this.querySelector('product-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`) || html.querySelector(`product-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        const mediaGallery = this.querySelector('media-gallery');
        if (mediaGallery != null) {
          mediaGallery.setActiveMedia?.(`${this.dataset.section}-${variantFeaturedMediaId}`, true);
        } else {
          this.querySelector('[id^="Slider-"]')?.scrollTo({ left: 0 });
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const input = this.quantityInput || this.quantityInputSticky;
        const data = {
          cartQuantity: input?.dataset.cartQuantity ? parseInt(input.dataset.cartQuantity) : 0,
          min: input?.dataset.min ? parseInt(input.dataset.min) : 1,
          max: input?.dataset.max ? parseInt(input.dataset.max) : null,
          step: input?.step ? parseInt(input.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        if(this.quantityInput) this.quantityInput.min = min;
        if(this.quantityInputSticky) this.quantityInputSticky.min = min;

        if (max) {
          if(this.quantityInput) this.quantityInput.max = max;
          if(this.quantityInputSticky) this.quantityInputSticky.max = max;
        } else {
          this.quantityInput?.removeAttribute('max');
          this.quantityInputSticky?.removeAttribute('max');
        }
        if(this.quantityInput) this.quantityInput.value = min;
        if(this.quantityInputSticky) this.quantityInputSticky.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        //this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            //this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput && !this.quantityInputSticky) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`) || html.getElementById(`Quantity-Form-${sectionId}-sticky`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm?.querySelector(selector);
          const currentSticky = this.quantityFormSticky?.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if(!updated) continue;
          if (!current && !currentSticky) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current?.setAttribute(attribute, valueUpdated);
                currentSticky?.setAttribute(attribute, valueUpdated);
              } else {
                current?.removeAttribute(attribute);
                currentSticky?.removeAttribute(attribute);
              }
            }
          } else {
            if(current) current.innerHTML = updated.innerHTML;
            if(currentSticky) currentSticky.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects[data-primary]');
      }

      get variantSelectorsAlt() {
        return this.querySelector('variant-selects[data-sticky]');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
